import { List } from 'immutable';
import * as convert from 'xml-js';
import pick from 'lodash.pick';

import { EspressoClass, EspressoTest, TestCase, TestResult, Testsuite, Property, Failure } from './records';
import {
  RDC_NATIVE_FRAMEWORKS_TEST_STATUS_MAPPING,
  NATIVE_FRAMEWORKS_SUCCESS_STATUS,
  NATIVE_FRAMEWORKS_FAILURE_STATUS,
} from './constants';

/**
 * Parses Espresso command logs into a collection of Espresso records.
 * @param {Object} commands
 * @param {Object[]} commands.tests
 * @returns {Immutable.List<EspressoClass>}
 */
const parseEspressoLog = (commands) => {
  let previousClass = null;
  let classId = 0;

  /* eslint no-param-reassign: 0 */
  return commands.tests
    .map((test) => {
      test.test_number = parseInt(test.test_number, 10);
      return test;
    })
    .reduce((acc, test) => {
      // append empty class if name has changed
      if (test.class !== previousClass) {
        previousClass = test.class;
        acc = acc.push(new EspressoClass({ name: test.class, id: classId += 1 }));
      }

      // append test
      const tests = acc.last().get('tests');
      return acc.setIn(
        [acc.size - 1, 'tests'],
        tests.push(new EspressoTest(test)),
      );
    }, new List());
};

/**
 * Converts JUnit object into a TestResult record.
 * @param {Object} originalTestResult
 * @returns {TestResult}
 */
const convertTestResult2Immutable = (originalTestResult) => {
  const testsuites = [];
  let testNumber = 1;
  originalTestResult.testsuites.forEach((origTestSuite) => {
    // Fill testcases
    const testcases = [];
    origTestSuite.testcases.forEach((origTestCase) => {
      // Fill failures
      let failure = null;
      if (origTestCase.failure) {
        const flr = origTestCase.failure;
        failure = new Failure(
          {
            content: flr.content,
            message: flr.message,
            type: flr.type,
          });
      }
      const testcase = new TestCase({
        ...pick(origTestCase, ['key', 'name', 'time', 'classname']),
        failure,
        test_number: testNumber,
      });
      testNumber += 1;

      testcases.push(testcase);
    });

    // Fill properties
    const properties = origTestSuite.properties.map((prop) => (
      new Property({
        name: prop.name,
        value: prop.value,
      })
    ));

    const testsuite = new Testsuite({
      ...pick(origTestSuite, ['name', 'timestamp', 'tests', 'time', 'failures', 'id']),
      testcases: List(testcases),
      properties: List(properties),
    });

    testsuites.push(testsuite);
  });

  return new TestResult({
    ...pick(originalTestResult, ['name', 'tests', 'time', 'failures', 'errors', 'disabled']),
    testsuites: List(testsuites),
  });
};

/* eslint no-underscore-dangle: [2, { "allow": ["_attributes", "_cdata", "_nameId"] }] */
/**
 * Parses JUnit log content to Plain Old JavaScript Object.
 * @param {string} xmlContent
 * @returns {Object}
 */
const parseJUnitToPOJO = (xmlContent) => {
  const jsonContent = convert.xml2js(xmlContent, { compact: true, spaces: 4 });

  const testResult = pick(jsonContent.testsuites._attributes,
    ['name', 'time', 'tests', 'failures', 'errors', 'disabled']);

  // To support a single testsuite
  const originalTestsuite = Array.isArray(jsonContent.testsuites.testsuite) ?
    jsonContent.testsuites.testsuite : [jsonContent.testsuites.testsuite];
  const testsuites = originalTestsuite.map((testSuite) => {
    const testsuite = pick(testSuite._attributes, ['name', 'timestamp', 'tests', 'time', 'failures', 'id']);

    // Fill testcases
    let originalTestcases = testSuite.testcase || [];
    if (!Array.isArray(originalTestcases)) {
      originalTestcases = [testSuite.testcase];
    }

    const testcases = originalTestcases.map((testCase, index) => {
      const testcase = {
        key: index,
        name: testCase._attributes.name,
        time: testCase._attributes.time,
        classname: testCase._attributes.classname,
      };

      // Fill failures
      if (testCase.failure !== undefined) {
        const flr = testCase.failure;
        const content = flr._cdata || '';
        const failure = {
          content,
          message: (flr._attributes && flr._attributes.message) || content,
          type: (flr._attributes && flr._attributes.type) || '',
        };
        testcase.failure = failure;
      }
      return testcase;
    });

    testsuite.testcases = testcases;
    // Fill properties
    let properties;
    if (!testSuite.properties) {
      properties = [];
    } else {
      properties = testSuite.properties.property.map((prop) => (
        {
          name: prop._attributes.name,
          value: prop._attributes.value,
        }
      ));
    }

    testsuite.properties = properties;

    return testsuite;
  });

  testResult.testsuites = testsuites;

  return testResult;
};

/**
 * Parses JUnit file into a TestResult record.
 * Used for Cypress, Playwright and Testcafe.
 * @param {string} xmlContent
 * @returns {TestResult}
 */
const parseJUnit = (xmlContent) => convertTestResult2Immutable(parseJUnitToPOJO(xmlContent));

/**
 * Parses native log into a list of Espresso Record.
 * Used for Espresso(Emulator&RDC) and XCUITest(RDC).
 * @param {string} xmlContent
 * @returns {Immutable.List<EspressoClass>}
 */
const parseNativeFrameworkTestCases = (testCases) => {
  let classId = 0;
  let testId = 0;

  return testCases.reduce((acc, testCase) => {
    acc = acc.push(new EspressoClass({ name: testCase.className, id: classId += 1 }));
    const espressoTests = testCase.testCases.reduce((tests, test) => {
      tests = tests.push(new EspressoTest(
        {
          class: test.className,
          failure_reason: test.testError?.error,
          name: test.methodName,
          status: test.status,
          status_code: RDC_NATIVE_FRAMEWORKS_TEST_STATUS_MAPPING[test.status],
          test_number: testId += 1,
          start_time: test.startTime / 1000,
          duration: test.duration,
        }));
      return tests;
    }, List());

    return acc.setIn(
      [acc.size - 1, 'tests'],
      espressoTests);
  }, List());
};

/**
 * Parses JUnit file into a list of Espresso Record.
 * @param {string} xmlContent
 * @returns {Immutable.List<EspressoClass>}
 */
const parseXCUITestLog = (xmlContent) => {
  const testResult = parseJUnitToPOJO(xmlContent);
  const commands = [];
  let testNumber = 1;
  testResult.testsuites.forEach((testsuite, id) => {
    const tests = [];
    testsuite.testcases.forEach((testcase) => {
      tests.push(new EspressoTest({
        class: testsuite.name,
        failure_reason: testcase.failure?.content || testcase.failure?.message,
        name: testcase.name,
        status_code: testcase.failure ? 1 : 0,
        status: testcase.failure ?
          NATIVE_FRAMEWORKS_FAILURE_STATUS : NATIVE_FRAMEWORKS_SUCCESS_STATUS,
        test_number: testNumber,
        start_time: testcase.timestamp,
        duration: testcase.time,
      }));
      testNumber += 1;
    });
    commands.push(new EspressoClass({
      name: testsuite.name,
      tests: List(tests),
      id,
    }));
  });

  return List(commands);
};

/**
 * Filters the list of Espresso tests and returns only passed ones.
 * @param {Object} params
 * @param {EspressoClass} params.espressoClass
 * @returns {Immutable.List<EspressoTest>}
 */
const allTestsInClassPassed = ({ espressoClass }) => (
  !espressoClass.get('tests').find((test) => test.isFailed())
);

/**
 * @param {Object} params
 * @param {EspressoClass} params.espressoClass
 * @param {number} params.testId
 * @returns {number} Test ID.
 */
const findClassIdWithTestId = ({ testId, espressoClass }) => (
  espressoClass.find((espressoItem) => (
    espressoItem.get('tests').find((test) => test.test_number === testId)
  ))?.id
);

/**
 * @param {Object} params
 * @param {string} params.hash
 * @param {EspressoClass} params.espressoClass
 * @returns {{ classId: number, testId: number }}
 */
const findInitialTestId = ({ hash, espressoClass }) => {
  let result;
  const testId = Number.parseInt(hash, 10);

  if (!Number.isNaN(testId)) {
    return {
      testId,
      classId: findClassIdWithTestId({ testId, espressoClass }),
    };
  }

  for (let i = 0; i < espressoClass.size; i += 1) {
    const classItem = espressoClass.get(i);
    const failingTest = classItem.get('tests').find((test) => test.isFailed());
    if (failingTest) {
      result = {
        testId: failingTest.test_number,
        classId: classItem.id,
      };
      break;
    }
  }

  if (result) {
    return result;
  }

  // if all tests are OK, then select the 1st one
  return {
    testId: 1,
    classId: findClassIdWithTestId({ testId: 1, espressoClass }),
  };
};

export {
  allTestsInClassPassed,
  findClassIdWithTestId,
  findInitialTestId,
  parseEspressoLog,
  parseNativeFrameworkTestCases,
  parseJUnit,
  parseXCUITestLog,
};
